/**
 * HOW TO ADD A NEW UNIBROW
 * ========================
 *
 * 1. Backend:
 *    a. Create a Dismissible class in app/lib/dismissibles/
 *       - Implement show? method to determine when to display
 *       - See existing dismissibles for examples
 *
 *    b. Add to determine_active_unibrow in app/controllers/api/v0/unibrows_controller.rb
 *       - Position determines priority (earlier = higher priority)
 *       - Return { type: 'YourUnibrowName', props: { ... } }
 *
 *    c. Add to get_forced_unibrow for testing (same controller)
 *       - Allows admins to test with ?force_unibrow=YourUnibrowName
 *
 * 2. Frontend:
 *    a. Define props type in app/assets/javascripts/unibrows/types.ts
 *       export type YourUnibrowProps = { ... };
 *
 *    b. Add to UnibrowData type union in this file
 *       | { type: 'YourUnibrowName'; props: YourUnibrowProps }
 *
 *    c. Add case to switch statement in ActiveUnibrow
 *       - For simple unibrows: Use <Unibrow /> component inline
 *       - For complex unibrows: Create separate component file
 *
 * 3. Testing:
 *    - Visit any page with ?force_unibrow=YourUnibrowName (must be admin)
 *    - Check dismissal behavior works correctly
 *    - Verify priority order with other unibrows
 *
 */

import React from 'react';
import {
  QueryClientProvider,
  QueryClient,
  useQuery,
} from '@tanstack/react-query';

import routes from 'js/core_pre_webpack/routes';
import Unibrow from 'js/design_system/Unibrow';
import { recordDismissal } from 'js/utils/dismissibles';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';

import PaymentProblemAlertUnibrow from './PaymentProblemAlertUnibrow';
import AddSecondaryPaymentUnibrow from './AddSecondaryPaymentUnibrow';
import ChurnedUserReactivationUnibrow from './ChurnedUserReactivationUnibrow';
import SeasonalDiscountUnibrow from './seasonal_discount_unibrow/SeasonalDiscountUnibrow';
import UploadEncouragementUnibrow from './UploadEncouragementUnibrow';

import type {
  PaymentProblemAlertUnibrowProps,
  ChurnedUserReactivationUnibrowProps,
  SeasonalDiscountUnibrowProps,
  AbandonedCartUnibrowProps,
  UploadEncouragementUnibrowProps,
  SpecialIssueRecruitmentUnibrowProps,
  PaperClaimingUnibrowProps,
  JournalViewFeedbackUnibrowProps,
  JournalDraftReminderUnibrowProps,
  LetterReviewRequestUnibrowProps,
  LetterSubmitInvitationUnibrowProps,
  CiterightDraftPromoUnibrowProps,
} from './types';

const queryClient = new QueryClient();

/**
 * Discriminated union type defining all possible unibrow responses
 *
 * This type represents the API response from /api/v0/unibrows/active.
 * Each variant has a 'type' string and associated 'props' object.
 *
 * The type: null variant is returned when no unibrow should be shown.
 *
 * IMPORTANT: This type must stay in sync with the unibrows_controller's
 * determine_active_unibrow method and get_forced_unibrow methods
 */
type UnibrowData =
  | {
      type: 'PaymentProblemAlertUnibrow';
      props: PaymentProblemAlertUnibrowProps;
    }
  | {
      type: 'AddSecondaryPaymentUnibrow';
      props: Record<string, never>;
    }
  | {
      type: 'ChurnedUserReactivationUnibrow';
      props: ChurnedUserReactivationUnibrowProps;
    }
  | {
      type: 'SeasonalDiscountUnibrow';
      props: SeasonalDiscountUnibrowProps;
    }
  | {
      type: 'AbandonedCartUnibrow';
      props: AbandonedCartUnibrowProps;
    }
  | {
      type: 'UploadEncouragementUnibrow';
      props: UploadEncouragementUnibrowProps;
    }
  | {
      type: 'SpecialIssueRecruitmentUnibrow';
      props: SpecialIssueRecruitmentUnibrowProps;
    }
  | {
      type: 'PaperClaimingUnibrow';
      props: PaperClaimingUnibrowProps;
    }
  | {
      type: 'JournalViewFeedbackUnibrow';
      props: JournalViewFeedbackUnibrowProps;
    }
  | {
      type: 'JournalDraftReminderUnibrow';
      props: JournalDraftReminderUnibrowProps;
    }
  | {
      type: 'LetterReviewRequestUnibrow';
      props: LetterReviewRequestUnibrowProps;
    }
  | {
      type: 'LetterSubmitInvitationUnibrow';
      props: LetterSubmitInvitationUnibrowProps;
    }
  | {
      type: 'OnboardingPortfolioUnibrow';
      props: Record<string, never>;
    }
  | {
      type: 'ImpactHubUnibrow';
      props: Record<string, never>;
    }
  | {
      type: 'CiterightDraftPromoUnibrow';
      props: CiterightDraftPromoUnibrowProps;
    }
  | {
      type: null;
      props: null;
    };

type ActiveUnibrowProps = {
  limitedUnibrows?: boolean | null;
  checkForCoupon?: string | null;
};

/**
 * ActiveUnibrow - Automatically fetches and renders the appropriate unibrow component
 *
 * Flow:
 * 1. Component mounts and makes API call to /api/v0/unibrows/active
 * 2. Backend (UnibrowsController) determines which unibrow to show based on priority order
 * 3. API returns { type: 'UnibrowName', props: {...} }
 * 4. Component renders the appropriate component based on the type
 *
 * Props:
 * - limitedUnibrows: When true, excludes certain lower-priority unibrows
 * - checkForCoupon: When true, checks for premium discount coupon eligibility
 *
 * Query Params:
 * - force_unibrow: Forces a specific unibrow for testing (admin only)
 *                  Example: ?force_unibrow=PaymentProblemAlertUnibrow
 *
 */
const ActiveUnibrow: React.FC<ActiveUnibrowProps> = ({
  limitedUnibrows,
  checkForCoupon,
}) => {
  const { data, isLoading, error } = useQuery<UnibrowData>({
    queryKey: ['activeUnibrow', limitedUnibrows],
    queryFn: async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const force_unibrow = urlParams.get('force_unibrow');

      const url = routes.active_api_v0_unibrows_path({
        subdomain_param: 'api',
        force_unibrow,
        limited_unibrows: limitedUnibrows ?? false,
        ...(checkForCoupon ? { premium_discount_coupon: checkForCoupon } : {}),
      });

      const response = await fetchWithCsrf(url);
      if (!response.ok) {
        throw new Error('Failed to fetch active unibrow');
      }
      return response.json();
    },
    staleTime: Infinity,
  });

  if (isLoading || error || !data?.type) {
    return null;
  }

  const unibrowType = data.type;

  switch (unibrowType) {
    case 'PaymentProblemAlertUnibrow':
      return <PaymentProblemAlertUnibrow {...data.props} />;

    case 'AddSecondaryPaymentUnibrow':
      return <AddSecondaryPaymentUnibrow />;

    case 'ChurnedUserReactivationUnibrow':
      return <ChurnedUserReactivationUnibrow {...data.props} />;

    case 'SeasonalDiscountUnibrow':
      return <SeasonalDiscountUnibrow {...data.props} />;

    case 'AbandonedCartUnibrow':
      return (
        <Unibrow
          dismissibleName="AbandonedCartUnibrow"
          text={data.props.text}
          primaryButton={{
            text: 'Finish your upgrade',
            onClick: () => {
              window.location.href = data.props.ctaUrl;
            },
            iconRight: 'arrow_forward',
          }}
        />
      );

    case 'UploadEncouragementUnibrow':
      return <UploadEncouragementUnibrow />;

    case 'SpecialIssueRecruitmentUnibrow':
      return (
        <Unibrow
          dismissibleName={data.props.name}
          text={`You're invited to submit to ${data.props.specialIssueName}, a recently launched Special Issue in ${data.props.journalName}`}
          primaryButton={{
            text: 'View Special Issue',
            onClick: () => {
              window.location.href = data.props.specialIssueUrl;
            },
          }}
        />
      );

    case 'PaperClaimingUnibrow':
      return (
        <Unibrow
          dismissibleName="PaperClaimingUnibrow"
          text={data.props.text}
          primaryButton={{
            text: 'Add to Profile',
            onClick: () => {
              window.location.href = data.props.claimUrl;
            },
          }}
          secondaryButton={{
            text: 'This is not me',
            onClick: () => {
              window.location.href = data.props.anticlaimUrl;
            },
          }}
        />
      );

    case 'JournalViewFeedbackUnibrow':
      return (
        <Unibrow
          dismissibleName="JournalViewFeedbackUnibrow"
          eyebrowIcon="warning"
          eyebrowText="Action required"
          text={`You have feedback on your recent ${data.props.journalName} submission`}
          primaryButton={{
            text: 'View feedback',
            onClick: () => {
              window.location.href = data.props.viewUrl;
            },
          }}
        />
      );

    case 'JournalDraftReminderUnibrow':
      return (
        <Unibrow
          dismissibleName="JournalDraftReminderUnibrow"
          eyebrowIcon="warning"
          eyebrowText="Action required"
          text={`Your ${data.props.journalName} draft is waiting to be submitted.`}
          primaryButton={{
            text: 'View draft',
            onClick: () => {
              window.location.href = data.props.continueUrl;
            },
          }}
        />
      );

    case 'LetterReviewRequestUnibrow':
      return (
        <Unibrow
          dismissibleName="LetterReviewRequestUnibrow"
          text={`Your review is requested for ${data.props.title}, deadline ${data.props.deadline}.`}
          primaryButton={{
            text: 'Review now',
            onClick: () => {
              window.location.href = routes.letters_submission_path({
                obscured_id: data.props.obscured_id,
              });
            },
          }}
          secondaryButton={{
            text: 'Decline request',
            onClick: () => {
              fetchWithCsrf(
                routes.api_v0_letters_reviews_path({ subdomain_param: 'api' }),
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    letter_submission_id: data.props.id,
                    decision: 'unable_to_review',
                  }),
                },
              );
            },
          }}
        />
      );

    case 'LetterSubmitInvitationUnibrow':
      return (
        <Unibrow
          dismissibleName="LetterSubmitInvitationUnibrow"
          text={`You're invited to submit to ${data.props.journalName},  a new open access journal.`}
          primaryButton={{
            text: 'Learn more',
            onClick: () => {
              window.location.href = data.props.learnMoreUrl;
            },
          }}
        />
      );

    case 'OnboardingPortfolioUnibrow':
      return (
        <Unibrow
          dismissibleName="OnboardingPortfolioUnibrow"
          icon="laptop_windows"
          text="As a Premium member, you get a free Personal Website powered by your profile"
          primaryButton={{
            text: 'Activate your Personal Website',
            onClick: () => {
              window.location.href = routes.portfolio_creation_next_step_url();
            },
          }}
        />
      );

    case 'CiterightDraftPromoUnibrow': {
      const { ctaUrl, surveyUrl } = data.props;
      return (
        <Unibrow
          dismissibleName="CiterightDraftPromoUnibrow"
          icon="fact_check"
          text="Upload a draft to find references you may have missed"
          primaryButton={{
            text: 'Try CiteRight',
            onClick: () => {
              // Treat the click as a soft dismissal: it hides the banner now
              // but, unlike a permanent opt-out, lets it return as a nudge
              // until the user actually runs a manual report (or hits the
              // dismissal cap). See Dismissibles::CiterightDraftPromoUnibrow.
              recordDismissal('CiterightDraftPromoUnibrow');
              window.location.href = ctaUrl;
            },
            trackClick: {
              source: 'citeright_draft_promo_unibrow_cta',
              extra: { action: 'try_citeright_on_draft' },
            },
          }}
          secondaryButton={
            surveyUrl
              ? {
                  text: 'Not now',
                  onClick: () => {
                    // "Not interested" is a dismissal: without this the
                    // banner would re-appear on the next pageview
                    recordDismissal('CiterightDraftPromoUnibrow');
                    window.open(surveyUrl, '_blank', 'noopener');
                  },
                  trackClick: {
                    source: 'citeright_draft_promo_unibrow_survey',
                    extra: { action: 'open_not_interested_survey' },
                  },
                }
              : undefined
          }
        />
      );
    }

    case 'ImpactHubUnibrow':
      return (
        <Unibrow
          dismissibleName="ImpactHubUnibrow"
          eyebrowText="Your papers can reach 10x more researchers"
          icon="whatshot"
          text="The Impact Hub uses AI to transform your research into shareable content"
          primaryButton={{
            text: 'Try Impact Hub',
            onClick: () => {
              window.location.href = routes.impact_home_url();
            },
            trackClick: {
              source: 'impact_hub_unibrow_cta',
              extra: { action: 'explore_impact_hub' },
            },
          }}
        />
      );

    default:
      throw unibrowType satisfies never;
  }
};

type UnibrowChooserProps = {
  limitedUnibrows?: boolean;
};

const UnibrowChooser: React.FC<UnibrowChooserProps> = ({ limitedUnibrows }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ActiveUnibrow limitedUnibrows={limitedUnibrows} />
    </QueryClientProvider>
  );
};

export default UnibrowChooser;
